// Staff-side firm identity: what prints on invoices, court forms and
// client emails. The config is admin-only on the server and mirrors
// packages/server/src/config/attorneyConfig.ts field-for-field; the
// identity slice is open to every staff role and carries the module set.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { ModuleId } from "@duralex/shared";
import { getAuthHeaders } from "./auth";

export interface FirmAddress {
  street: string;
  aptSuite: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  postalCode: string;
  province: string;
}

export interface FirmConfig {
  firstName: string;
  middleName: string;
  lastName: string;
  fullName: string;
  suffix: string;
  barNumber: string;
  barState: string;
  eoirId: string;
  firmName: string;
  organizationName: string;
  address: FirmAddress;
  phone: string;
  mobile: string;
  fax: string;
  email: string;
  website: string;
  uscisOnlineAcct: string;
  timezone: string;
  logo: string;
}

export type FirmConfigPatch = Partial<Omit<FirmConfig, "address">> & {
  address?: Partial<FirmAddress>;
};

export async function fetchFirmConfig(): Promise<FirmConfig> {
  const res = await fetch("/api/admin/attorney-config", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetchFirmConfig: ${res.status}`);
  return (await res.json()) as FirmConfig;
}

export async function saveFirmConfig(patch: FirmConfigPatch): Promise<FirmConfig> {
  const res = await fetch("/api/admin/attorney-config", {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    credentials: "same-origin",
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    // The server's Zod message ("Logo exceeds the 300KB limit") is the
    // useful part - surface it instead of a bare status code.
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `saveFirmConfig: ${res.status}`);
  }
  return (await res.json()) as FirmConfig;
}

/** The chrome's display subset - what any staff may see of the identity. */
export interface FirmIdentity {
  firmName: string;
  attorneyFullName: string;
  barNumber: string;
  barState: string;
  website: string;
  /** The modules the firm has on; absent on an older server, which means all. */
  modules?: ModuleId[];
  /** A hash of the stored logo, null without one; absent on an older server. */
  logoVersion?: string | null;
}

/** Where the firm's logo is fetched from, keyed by its hash. */
export function firmLogoSrc(logoVersion: string | null | undefined): string | null {
  return logoVersion ? `/api/client/firm-logo?v=${encodeURIComponent(logoVersion)}` : null;
}

export async function fetchFirmIdentity(): Promise<FirmIdentity> {
  const res = await fetch("/api/firm/identity", {
    headers: { ...getAuthHeaders() },
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`fetchFirmIdentity: ${res.status}`);
  return (await res.json()) as FirmIdentity;
}

export function useFirmIdentity() {
  return useQuery({
    queryKey: ["firm-identity"],
    queryFn: fetchFirmIdentity,
    staleTime: 300_000,
  });
}

/** Whether a module is on; an unknown set counts as on, since the server
    refuses a write by itself. */
export function moduleOn(modules: ModuleId[] | undefined, id: ModuleId): boolean {
  return !modules || modules.includes(id);
}

export function useModuleOn(id: ModuleId): boolean {
  return moduleOn(useFirmIdentity().data?.modules, id);
}

/** "https://phillylawcenter.com/" -> "phillylawcenter.com" for chrome text. */
export function siteHostOf(website: string | undefined): string {
  return (website ?? "").replace(/^https?:\/\//, "").replace(/\/.*$/, "");
}

export function useFirmConfig(enabled = true) {
  return useQuery({
    queryKey: ["firm-config"],
    queryFn: fetchFirmConfig,
    staleTime: 60_000,
    enabled,
  });
}

export function useSaveFirmConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveFirmConfig,
    onSuccess: (cfg) => {
      qc.setQueryData(["firm-config"], cfg);
      // The chrome shows a projection of this config - refetch it.
      void qc.invalidateQueries({ queryKey: ["firm-identity"] });
    },
  });
}
